import {
  RECEIVED_LAW_STATUS_LABEL,
  type DoctrineInput,
  type DoctrineRecord,
  type ReceivedLawInput,
  type ReceivedLawRecord,
  type ReceivedLawStatus,
} from '@buakei/types';
import { Badge, Button, EmptyState, ErrorState, LoadingState } from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useAuth } from '@/auth/session';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { registerApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Received-law register and doctrine records (ADR-041). Editors draft; legal reviewers record
 * established applicability, and approve doctrine records written by someone else. External
 * research uses established entries for its labels and approved doctrines as guidance.
 */

function useEditorialTokens() {
  const { session } = useAuth();
  const tokens = useReaderTokens();
  return session?.experiences.includes('editorial') ? tokens : null;
}

const field = 'rounded-md border border-line-200 bg-white p-2 text-ink-900';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink-900">
        {label}
      </label>
      {children(id)}
      {hint ? <span className="text-xs text-muted-600">{hint}</span> : null}
    </div>
  );
}

const KIND_LABEL: Record<ReceivedLawInput['kind'], string> = {
  statute: 'Statute',
  common_law: 'Common-law rule',
  equity: 'Equitable doctrine',
  other: 'Other',
};

const EMPTY_LAW: ReceivedLawInput = {
  name: '',
  kind: 'statute',
  originalJurisdiction: 'England',
  date: '',
  urlPrefix: '',
  receptionBasis: '',
  sierraLeoneAuthority: '',
  laterModification: '',
  notes: '',
  status: 'unconfirmed',
};

function ReceivedLawForm({
  initial,
  canApprove,
  onSave,
  onCancel,
  saving,
}: {
  initial: ReceivedLawInput;
  canApprove: boolean;
  onSave: (input: ReceivedLawInput) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [v, setV] = useState(initial);
  const set = (k: keyof ReceivedLawInput) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value });
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-soft-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(canApprove ? v : { ...v, status: 'unconfirmed' });
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name">
          {(id) => (
            <input
              id={id}
              value={v.name}
              onChange={set('name')}
              maxLength={200}
              required
              className={field}
            />
          )}
        </Field>
        <Field label="Kind">
          {(id) => (
            <select id={id} value={v.kind} onChange={set('kind')} className={field}>
              {(Object.keys(KIND_LABEL) as ReceivedLawInput['kind'][]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Original jurisdiction">
          {(id) => (
            <input
              id={id}
              value={v.originalJurisdiction}
              onChange={set('originalJurisdiction')}
              maxLength={120}
              className={field}
            />
          )}
        </Field>
        <Field label="Date">
          {(id) => (
            <input
              id={id}
              value={v.date}
              onChange={set('date')}
              maxLength={40}
              placeholder="1677"
              className={field}
            />
          )}
        </Field>
      </div>
      <Field
        label="legislation.gov.uk address"
        hint="Optional. External research uses this entry for pages whose address starts with it."
      >
        {(id) => (
          <input
            id={id}
            value={v.urlPrefix}
            onChange={set('urlPrefix')}
            maxLength={300}
            placeholder="https://www.legislation.gov.uk/aep/Cha2/29/3"
            className={field}
          />
        )}
      </Field>
      <Field label="Reception basis">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.receptionBasis}
            onChange={set('receptionBasis')}
            maxLength={1000}
            required
            className={field}
          />
        )}
      </Field>
      <Field label="Sierra Leone authority applying it">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.sierraLeoneAuthority}
            onChange={set('sierraLeoneAuthority')}
            maxLength={1000}
            className={field}
          />
        )}
      </Field>
      <Field label="Later modification or displacement">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.laterModification}
            onChange={set('laterModification')}
            maxLength={1000}
            className={field}
          />
        )}
      </Field>
      <Field label="Editorial notes">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.notes}
            onChange={set('notes')}
            maxLength={2000}
            className={field}
          />
        )}
      </Field>
      <Field
        label="Status"
        hint={
          canApprove
            ? undefined
            : 'Only a legal reviewer can set a status other than "requires confirmation". Saving sends the entry for review.'
        }
      >
        {(id) => (
          <select
            id={id}
            value={canApprove ? v.status : 'unconfirmed'}
            disabled={!canApprove}
            onChange={set('status')}
            className={field}
          >
            {(Object.keys(RECEIVED_LAW_STATUS_LABEL) as ReceivedLawStatus[]).map((s) => (
              <option key={s} value={s}>
                {RECEIVED_LAW_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <div className="flex gap-3">
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function ReceivedLawPage() {
  const tokens = useEditorialTokens();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const list = useQuery({
    queryKey: ['registers', 'received-law'],
    queryFn: () => registerApi.receivedLaw(tokens!),
    enabled: tokens !== null,
  });
  const save = useMutation({
    mutationFn: ({ input, id }: { input: ReceivedLawInput; id?: string }) =>
      registerApi.saveReceivedLaw(tokens!, input, id),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['registers', 'received-law'] });
    },
  });
  if (!tokens) {
    return (
      <Page title="Received law">
        <EmptyState title="The received-law register is for the Editorial console" />
      </Page>
    );
  }
  const data = list.data;
  return (
    <Page
      title="Received law"
      description="English statutes and rules, and whether they apply in Sierra Leone. External research labels a UK statute as applying only when a legal reviewer has recorded it here as established."
      width="wide"
      actions={
        data?.canEdit && editing === null ? (
          <Button onClick={() => setEditing('new')}>
            <Plus aria-hidden /> Add an entry
          </Button>
        ) : null
      }
    >
      {list.isPending ? <LoadingState label="Loading the register" /> : null}
      {list.isError ? (
        <ErrorState title="The register could not be loaded" onRetry={() => void list.refetch()}>
          {list.error.message}
        </ErrorState>
      ) : null}
      {editing === 'new' && data ? (
        <ReceivedLawForm
          initial={EMPTY_LAW}
          canApprove={data.canApprove}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(input) => save.mutate({ input })}
        />
      ) : null}
      {save.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {save.error.message}
        </p>
      ) : null}
      {data && data.entries.length === 0 ? <EmptyState title="No entries yet" /> : null}
      {data && data.entries.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {data.entries.map((e: ReceivedLawRecord) =>
            editing === e.id ? (
              <li key={e.id}>
                <ReceivedLawForm
                  initial={e}
                  canApprove={data.canApprove}
                  saving={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(input) => save.mutate({ input, id: e.id })}
                />
              </li>
            ) : (
              <li
                key={e.id}
                className="flex flex-col gap-2 rounded-lg border border-line-200 bg-white p-4 text-sm"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-navy-900">{e.name}</span>
                  <Badge tone={e.status === 'established' ? 'success' : 'plain'}>
                    {RECEIVED_LAW_STATUS_LABEL[e.status]}
                  </Badge>
                </span>
                <span className="text-muted-600">
                  {KIND_LABEL[e.kind]} · {e.originalJurisdiction}
                  {e.date ? `, ${e.date}` : ''}
                  {e.reviewedByName ? ` · status set by ${e.reviewedByName}` : ''}
                </span>
                <p className="text-ink-900">
                  <strong>Reception basis:</strong> {e.receptionBasis}
                </p>
                {e.sierraLeoneAuthority ? (
                  <p className="text-ink-900">
                    <strong>Sierra Leone authority:</strong> {e.sierraLeoneAuthority}
                  </p>
                ) : null}
                {e.laterModification ? (
                  <p className="text-ink-900">
                    <strong>Later modification:</strong> {e.laterModification}
                  </p>
                ) : null}
                {e.notes ? <p className="text-muted-600">{e.notes}</p> : null}
                {data.canEdit && editing === null ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="self-start"
                    onClick={() => setEditing(e.id)}
                    aria-label={`Edit ${e.name}`}
                  >
                    Edit
                  </Button>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </Page>
  );
}

const EMPTY_DOCTRINE: DoctrineInput = {
  name: '',
  description: '',
  sierraLeoneRelevance: '',
  leadingAuthorities: [],
  sierraLeoneCases: [],
  notes: '',
  reviewDate: '',
};

type Authority = DoctrineInput['leadingAuthorities'][number];

function AuthorityRows({
  legend,
  rows,
  onChange,
}: {
  legend: string;
  rows: Authority[];
  onChange: (rows: Authority[]) => void;
}) {
  const update = (i: number, k: keyof Authority, value: string) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [k]: value } : r)));
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-ink-900">{legend}</legend>
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid gap-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
        >
          <input
            aria-label={`${legend} ${i + 1}: name`}
            value={r.name}
            onChange={(e) => update(i, 'name', e.target.value)}
            maxLength={200}
            placeholder="Case or statute name"
            className={field}
          />
          <input
            aria-label={`${legend} ${i + 1}: citation`}
            value={r.citation}
            onChange={(e) => update(i, 'citation', e.target.value)}
            maxLength={200}
            placeholder="Citation"
            className={field}
          />
          <input
            aria-label={`${legend} ${i + 1}: jurisdiction`}
            value={r.jurisdiction}
            onChange={(e) => update(i, 'jurisdiction', e.target.value)}
            maxLength={120}
            placeholder="Jurisdiction"
            className={field}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
      ))}
      {rows.length < 10 ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => onChange([...rows, { name: '', citation: '', jurisdiction: '' }])}
        >
          Add
        </Button>
      ) : null}
    </fieldset>
  );
}

function DoctrineForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: DoctrineInput;
  onSave: (input: DoctrineInput) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [v, setV] = useState(initial);
  const set =
    (k: 'name' | 'description' | 'sierraLeoneRelevance' | 'notes' | 'reviewDate') =>
    (e: { target: { value: string } }) =>
      setV({ ...v, [k]: e.target.value });
  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-line-200 bg-soft-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          ...v,
          leadingAuthorities: v.leadingAuthorities.filter((a) => a.name.trim()),
          sierraLeoneCases: v.sierraLeoneCases.filter((a) => a.name.trim()),
        });
      }}
    >
      <Field
        label="Doctrine"
        hint="External research uses an approved record when every word of this name appears in the issue it researches."
      >
        {(id) => (
          <input
            id={id}
            value={v.name}
            onChange={set('name')}
            maxLength={120}
            required
            className={field}
          />
        )}
      </Field>
      <Field label="Description">
        {(id) => (
          <textarea
            id={id}
            rows={3}
            value={v.description}
            onChange={set('description')}
            maxLength={2000}
            required
            className={field}
          />
        )}
      </Field>
      <Field label="Relevance in Sierra Leone">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.sierraLeoneRelevance}
            onChange={set('sierraLeoneRelevance')}
            maxLength={2000}
            className={field}
          />
        )}
      </Field>
      <AuthorityRows
        legend="Leading authorities"
        rows={v.leadingAuthorities}
        onChange={(rows) => setV({ ...v, leadingAuthorities: rows })}
      />
      <AuthorityRows
        legend="Sierra Leone cases applying it"
        rows={v.sierraLeoneCases}
        onChange={(rows) => setV({ ...v, sierraLeoneCases: rows })}
      />
      <Field label="Editorial notes">
        {(id) => (
          <textarea
            id={id}
            rows={2}
            value={v.notes}
            onChange={set('notes')}
            maxLength={2000}
            className={field}
          />
        )}
      </Field>
      <Field label="Review by (date)">
        {(id) => (
          <input
            id={id}
            type="date"
            value={v.reviewDate}
            onChange={set('reviewDate')}
            className={field}
          />
        )}
      </Field>
      <div className="flex gap-3">
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? 'Saving…' : 'Save as draft'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const DOCTRINE_STATUS: Record<DoctrineRecord['status'], string> = {
  draft: 'Draft: awaiting approval',
  approved: 'Approved: guiding external research',
  retired: 'Retired',
};

export function DoctrinesPage() {
  const tokens = useEditorialTokens();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const key = ['registers', 'doctrines'];
  const list = useQuery({
    queryKey: key,
    queryFn: () => registerApi.doctrines(tokens!),
    enabled: tokens !== null,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const save = useMutation({
    mutationFn: ({ input, id }: { input: DoctrineInput; id?: string }) =>
      registerApi.saveDoctrine(tokens!, input, id),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approve' | 'retire' }) =>
      registerApi.decideDoctrine(tokens!, id, decision),
    onSuccess: refresh,
  });
  if (!tokens) {
    return (
      <Page title="Doctrines">
        <EmptyState title="Doctrine records are for the Editorial console" />
      </Page>
    );
  }
  const data = list.data;
  const error = save.error ?? decide.error;
  return (
    <Page
      title="Doctrines"
      description="Short, reviewed notes on common-law and equitable doctrines and how they stand in Sierra Leone. An approved record is given to the AI as guidance when external research concerns that doctrine; it is never cited as a source."
      width="wide"
      actions={
        data?.canEdit && editing === null ? (
          <Button onClick={() => setEditing('new')}>
            <Plus aria-hidden /> Add a doctrine
          </Button>
        ) : null
      }
    >
      {list.isPending ? <LoadingState label="Loading doctrine records" /> : null}
      {list.isError ? (
        <ErrorState
          title="Doctrine records could not be loaded"
          onRetry={() => void list.refetch()}
        >
          {list.error.message}
        </ErrorState>
      ) : null}
      {editing === 'new' ? (
        <DoctrineForm
          initial={EMPTY_DOCTRINE}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(input) => save.mutate({ input })}
        />
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-ink-900">
          {error.message}
        </p>
      ) : null}
      {data && data.entries.length === 0 ? <EmptyState title="No doctrine records yet" /> : null}
      {data && data.entries.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {data.entries.map((d) =>
            editing === d.id ? (
              <li key={d.id}>
                <DoctrineForm
                  initial={d}
                  saving={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(input) => save.mutate({ input, id: d.id })}
                />
              </li>
            ) : (
              <li
                key={d.id}
                className="flex flex-col gap-2 rounded-lg border border-line-200 bg-white p-4 text-sm"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-navy-900">{d.name}</span>
                  <Badge tone={d.status === 'approved' ? 'success' : 'plain'}>
                    {DOCTRINE_STATUS[d.status]}
                  </Badge>
                </span>
                <span className="text-muted-600">
                  Drafted by {d.createdByName ?? 'an editor'}
                  {d.approvedByName ? ` · approved by ${d.approvedByName}` : ''}
                  {d.reviewDate ? ` · review by ${d.reviewDate}` : ''}
                </span>
                <p className="text-ink-900">{d.description}</p>
                {d.sierraLeoneRelevance ? (
                  <p className="text-ink-900">
                    <strong>In Sierra Leone:</strong> {d.sierraLeoneRelevance}
                  </p>
                ) : null}
                {d.sierraLeoneCases.length ? (
                  <p className="text-ink-900">
                    <strong>Sierra Leone cases:</strong>{' '}
                    {d.sierraLeoneCases.map((c) => `${c.name} ${c.citation}`.trim()).join('; ')}
                  </p>
                ) : null}
                {d.leadingAuthorities.length ? (
                  <p className="text-ink-900">
                    <strong>Leading authorities:</strong>{' '}
                    {d.leadingAuthorities
                      .map((a) => `${a.name} ${a.citation} (${a.jurisdiction})`.trim())
                      .join('; ')}
                  </p>
                ) : null}
                {editing === null ? (
                  <div className="flex flex-wrap items-center gap-3">
                    {data.canEdit ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setEditing(d.id)}
                        aria-label={`Edit ${d.name}`}
                      >
                        Edit
                      </Button>
                    ) : null}
                    {data.canApprove && d.status === 'draft' ? (
                      <Button
                        size="sm"
                        disabled={decide.isPending || d.createdByUid === session?.uid}
                        onClick={() => decide.mutate({ id: d.id, decision: 'approve' })}
                        aria-label={`Approve ${d.name}`}
                      >
                        Approve
                      </Button>
                    ) : null}
                    {data.canApprove && d.status !== 'retired' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={decide.isPending}
                        onClick={() => decide.mutate({ id: d.id, decision: 'retire' })}
                        aria-label={`Retire ${d.name}`}
                      >
                        Retire
                      </Button>
                    ) : null}
                    {data.canApprove && d.status === 'draft' && d.createdByUid === session?.uid ? (
                      <span className="text-muted-600">
                        Another legal reviewer must approve a record you wrote.
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </Page>
  );
}
